import { faCheck, faClipboardList, faExclamationCircle, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { hasValue, styles } from "@repo/helpers";
import { Button, ButtonSize, ButtonTypes, Tabs, TabsList, TabsTrigger } from "@repo/ui";
import { useCallback, useMemo, useState } from "react";

import { sectionIsVisible } from "..";
import { useValidationContext } from "../contexts/validation";
import DynamicComponent from "../dynamicComponent";
import { MemoizeWithSectionsByReference } from "../helpers/memo";
import { byOrdinal, isVisible } from "../helpers/sectionMapping";
import { ITERATED_SECTION_TYPES, sectionType } from "../models/enums";
import { MappedComponent, MappedSection } from "../models/mappedComponent";
import style from "./style.module.scss";
import { Props } from "./types";

const DynamicSection = MemoizeWithSectionsByReference(DynamicSectionInternal);
export default DynamicSection;

function DynamicSectionInternal(props: Props) {
	const {
		section,
		underParentObj,
		scrollToField: scrollToFieldFn,
		summaryEditPageExists,
		hideChildSections,
		onSummarySection,
		wrapperClass,
		hideSectionNames,
		...rest
	} = props;

	const sectionVisible = section.visible;
	const { subSectionLayout } = section;
	const hasVisibleComponents = useMemo(() => section.components.filter(isVisible).length, [section.components]);
	const childSections = useMemo(() => section.childSections.filter(sectionIsVisible), [section.childSections]);
	const hasVisibleSections = childSections.length;
	const entrySection = underParentObj ? undefined : section.linkName;
	const scrollToField = useCallback(scrollToFieldFn(section.label), [section.label]);
	const [tabIndex, setTabIndex] = useState<string>("0");
	const { hideValidation } = useValidationContext();

	if (!sectionVisible) return null;
	if (!hasVisibleComponents && !hasVisibleSections) return null;
	//Just like in mobile list, if summary section and only one child, fold up container

	const body =
		onSummarySection && !section.components.length && childSections.length === 1 ? (
			getSectionComponents()
		) : (
			<>
				{renderTitle()}
				{getSectionComponents()}
				<AddItemButton {...props} />
			</>
		);

	const classNames = styles(
		style.section,
		wrapperClass,
		section.section.type === sectionType.summary && style.summarySection,
	);
	return (
		<div id={section.linkName} data-entrysection={entrySection} key={section.id} className={classNames}>
			{body}
		</div>
	);

	function renderTitle() {
		if (hideSectionNames) return null;
		if (underParentObj)
			return (
				<h4 className={style.title}>
					<RemoveItemButton {...props} />{" "}
					<span>
						<SectionLabel section={section} />
					</span>
					<EditButton />
				</h4>
			);
		if (section.label)
			return (
				<h3 className={style.title}>
					<SectionLabel section={section} />
					<EditButton />
				</h3>
			);
	}

	function EditButton() {
		if (!onSummarySection) return null;
		//TODO: if on summary, but jump doesn't exist maybe display some text like automatically entered?
		if (!summaryEditPageExists(section.label)) return null;

		return (
			<span>
				<Button onClick={scrollToField} className={style.editButton}>
					Edit
				</Button>
			</span>
		);
	}

	function getSectionComponents() {
		const { index } = props;
		const sectionPropertyName = section.propertyName;
		const onSummarySection = props.onSummarySection || section.section.type === sectionType.summary;

		const columnGroups = [...section.components].sort(byOrdinal).groupBy((c) => c.column);

		const columnCount = columnGroups.size;
		const components = Array.from(columnGroups.values()).filter(hasValue).map(renderGroupedComponents);
		const columnLayout = columnCount > 1 || subSectionLayout === "column";
		const className = styles(columnLayout && style.groupedSection);
		return (
			<div key={index} className={className}>
				{components}
				{renderSubSections()}
			</div>
		);

		function renderGroupedComponents(componentGroup: MappedComponent[]) {
			if (!componentGroup.filter((g) => g.visible).length) return null;
			const groupComponents = componentGroup.map((component: MappedComponent) => (
				<DynamicComponent
					key={component.id}
					{...rest}
					component={component}
					clickable
					parentPropertyName={sectionPropertyName}
					scrollToField={scrollToField}
					data-column={component.column}
				/>
			));
			const sizes = componentGroup.map((c) => c.extendedProperties.size).filter(hasValue);
			let classNames = [style.sectionColumn];
			if (sizes.includes("large")) classNames.push(style.sectionColumnLg);
			else if (sizes.includes("medium")) classNames.push(style.sectionColumnMd);
			if (onSummarySection) classNames.push(style.summarySection);
			return (
				<div className={styles(...classNames)} key={componentGroup[0].id}>
					{groupComponents}
				</div>
			);
		}

		function renderSubSections() {
			if (hideChildSections || !section.childSections?.length) return null;
			const iterated = ITERATED_SECTION_TYPES.includes(section.section.type);
			const groupClass = styles(
				section.subSectionLayout === "column" && style.sectionColumn,
				onSummarySection && style.summarySubSection,
			);
			if (section.subSectionLayout?.includes("tab")) {
				const visibleSubSections = section.childSections.filter(sectionIsVisible);

				if (visibleSubSections.length > 1)
					return (
						<Tabs value={tabIndex} onValueChange={setTabIndex}>
							<TabsList>{[...visibleSubSections].sort(byOrdinal).map(renderSubSectionTab)}</TabsList>
							{renderSubSection(visibleSubSections[tabIndex], parseInt(tabIndex), true)}
						</Tabs>
					);
			}

			return [...section.childSections].sort(byOrdinal).map(renderSubSectionFull);
			function renderSubSectionTab(subSection: MappedSection, index: number) {
				return (
					<TabsTrigger key={subSection.label} value={index.toString()}>
						<SectionLabel section={subSection} showValidation={!hideValidation} />
					</TabsTrigger>
				);
			}
			function renderSubSectionFull(subSection: MappedSection, index: number) {
				return renderSubSection(subSection, index, false);
			}
			function renderSubSection(subSection: MappedSection, index: number, hideSectionNames: boolean) {
				return (
					<DynamicSection
						{...props}
						section={subSection}
						wrapperClass={groupClass}
						index={index}
						hideSectionNames={hideSectionNames}
						onSummarySection={onSummarySection}
						parentPropertyName={sectionPropertyName}
						underParentObj={iterated}
						key={index}
					/>
				);
			}
		}
	}
}

function SectionLabel(props: { section: MappedSection; showValidation?: boolean }) {
	const { section, showValidation } = props;
	const { validationResults } = useValidationContext();

	const componentErrors = (component: MappedComponent) => validationResults.getError(component);
	const sectionErrors = (section: MappedSection): string[] =>
		section.components
			.map(componentErrors)
			.concat(section.childSections.map(sectionErrors).flat())
			.filter((e) => e);
	// Only needed for the validation icon, so only walked when that icon can show
	const getErrors = () => sectionErrors(section).join(".\r\n");

	return (
		<div className={style.sectionLabel}>
			{getIcon()} <span>{section.label}</span>
		</div>
	);

	function getIcon() {
		switch (section.status) {
			case "Incomplete":
				return <FontAwesomeIcon icon={faClipboardList} color={"orange"} size={"lg"} title={section.status} />;
			case "Fail":
				return <FontAwesomeIcon icon={faCheck} color={"red"} size={"lg"} title={section.status} />;
			case "Pass":
				return <FontAwesomeIcon icon={faCheck} color={"green"} size={"lg"} title={section.status} />;
			default: {
				const errors = showValidation ? getErrors() : "";
				if (showValidation && errors)
					return <FontAwesomeIcon icon={faExclamationCircle} color={"red"} title={errors} />;
				return undefined;
			}
		}
	}
}

function AddItemButton(props: Pick<Props, "section" | "addItem" | "readOnly">) {
	const { section, addItem, readOnly } = props;

	if (!addItem) return null;
	if (!Array.isArray(section.propertyValue)) return null;
	if (section.addRemoveSectionHidden) return null;
	if (section.section.type !== sectionType.iteratedEditable) return null;

	return <AddItemButtonInternal section={section} addItem={addItem} readOnly={readOnly} />;
}

function AddItemButtonInternal(props: Pick<Props, "section" | "addItem" | "readOnly">) {
	const { section, addItem, readOnly } = props;
	const label = useMemo(() => `Add ${section.label.replace(/s$/, "")}`, [section.label]);

	const addItemFn = useCallback(addItem(section), [addItem, section]);

	return (
		<Button onClick={addItemFn} size={ButtonSize.large} disabled={readOnly}>
			<FontAwesomeIcon icon={faPlus} /> {label}
		</Button>
	);
}

function RemoveItemButton(props: Props) {
	const { section, removeItem, readOnly } = props;

	if (!removeItem) return null;
	if (section.iteratedTotal < 2) return null;
	if (section.addRemoveSectionHidden) return null;
	if (section.section.type !== sectionType.iteratedEditable) return null;

	return <RemoveItemButtonInternal section={section} removeItem={removeItem} readOnly={readOnly} />;
}

function RemoveItemButtonInternal(props: Pick<Props, "section" | "removeItem" | "readOnly">) {
	const { section, removeItem, readOnly } = props;
	//This is an assumption that the property value has an index
	const id = section.propertyValue.index;
	const label = useMemo(() => `Remove ${section.label.replace(/s$/, "")}`, [section.label]);

	const removeItemSection = useCallback(removeItem(section), [removeItem, section]);
	const removeItemFn = useCallback(removeItemSection?.(id), [removeItemSection, id]);
	if (!removeItemFn) return null;

	return (
		<Button
			size={ButtonSize.large}
			types={[ButtonTypes.Danger]}
			title={label}
			onClick={removeItemFn}
			tabIndex={0}
			disabled={readOnly}
		>
			<FontAwesomeIcon icon={faTimes} title={label} />
		</Button>
	);
}
